import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MapPin, Building, Layers, Grid3X3, Box, Trash2, Edit2, Check, X, RefreshCw, ChevronRight, ChevronDown, Lock, Unlock, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const LocationSetup = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tree');
  
  // Create dialog state
  const [createDialog, setCreateDialog] = useState({ open: false, level: 'hall' });
  const [formData, setFormData] = useState({
    hall_name: '',
    floor_name: '',
    row_name: '',
    position_name: '',
    capacity_kg: '',
    description: ''
  });
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, location: null });
  const [editData, setEditData] = useState({ capacity_kg: '', description: '', status: '' });
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, location: null });
  
  // Hierarchy state
  const [halls, setHalls] = useState([]);
  const [selectedHall, setSelectedHall] = useState('');
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState('');
  
  // Expanded nodes in tree view
  const [expanded, setExpanded] = useState({});
  const [highlight, setHighlight] = useState('');

  // ── Location Lock / Release (mirrors SFG/FG bin locks) ──
  const [locksByLocId, setLocksByLocId] = useState({});   // location_id -> lock
  const [lockSummary, setLockSummary] = useState([]);
  const [lockDialog, setLockDialog] = useState({ open: false, target: null }); // target: {location_id, footprint, level}
  const [lockPutAway, setLockPutAway] = useState(false);
  const [lockPicking, setLockPicking] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [savingLock, setSavingLock] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const fetchLocks = async () => {
    try {
      const res = await greigeStorageApi.getLocationLocks();
      const map = {};
      (res.locks || []).forEach((l) => { map[l.location_id] = l; });
      setLocksByLocId(map);
    } catch {
      setLocksByLocId({});
    }
  };

  const fetchLockSummary = async () => {
    try {
      const res = await greigeStorageApi.getLocationLocksSummary();
      setLockSummary(res.summary || []);
    } catch {
      setLockSummary([]);
    }
  };

  const normFp = (s) => (s || '').replace(/\s*>\s*/g, ' > ').trim();

  // Effective lock for a node: its own lock, or an ancestor's lock (cascade).
  const getEffectiveLock = (locationId, footprint) => {
    const direct = locksByLocId[locationId];
    if (direct && (direct.block_put_away || direct.block_picking)) {
      return { lock: direct, inherited: false };
    }
    const fp = normFp(footprint);
    for (const l of Object.values(locksByLocId)) {
      if (!l || !(l.block_put_away || l.block_picking)) continue;
      const p = normFp(l.footprint);
      if (p && fp !== p && fp.startsWith(p + ' > ')) {
        return { lock: l, inherited: true };
      }
    }
    return { lock: null, inherited: false };
  };

  const openLockDialog = (node) => {
    const own = locksByLocId[node.location_id];
    setLockPutAway(own?.block_put_away || false);
    setLockPicking(own?.block_picking || false);
    setLockReason(own?.reason || '');
    setLockDialog({ open: true, target: { location_id: node.location_id, footprint: node.footprint, level: node.level } });
  };

  const saveLock = async () => {
    if (!lockDialog.target) return;
    try {
      setSavingLock(true);
      await greigeStorageApi.setLocationLock({
        location_id: lockDialog.target.location_id,
        block_put_away: lockPutAway,
        block_picking: lockPicking,
        reason: lockReason,
      });
      const released = !lockPutAway && !lockPicking;
      toast.success(released ? 'Location released' : 'Location locked');
      setLockDialog({ open: false, target: null });
      await Promise.all([fetchLocks(), fetchLockSummary()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update lock');
    } finally {
      setSavingLock(false);
    }
  };

  const handleResync = async () => {
    try {
      setResyncing(true);
      const res = await greigeStorageApi.resyncLocationLockStatus();
      toast.success(`Resynced ${res.inventory_rows_updated} stock rows across ${res.active_locks} locks`);
      await fetchLockSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resync');
    } finally {
      setResyncing(false);
    }
  };

  const LockControl = ({ node }) => {
    const { lock, inherited } = getEffectiveLock(node.location_id, node.footprint);
    const isLocked = !!lock;
    const parts = [];
    if (isLocked) {
      if (lock.block_put_away) parts.push('Put-Away');
      if (lock.block_picking) parts.push('Picking');
    }
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {isLocked && (
          <span
            className={`text-[9px] px-1 py-0.5 rounded font-semibold ${inherited ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}
            title={`${inherited ? 'Inherited from ' + lock.node_label + ' — ' : ''}Blocked: ${parts.join(' + ')}${lock.reason ? ' · ' + lock.reason : ''}`}
            data-testid={`gs-lock-badge-${node.location_id}`}
          >
            {lock.block_put_away ? 'PA' : ''}{lock.block_put_away && lock.block_picking ? '+' : ''}{lock.block_picking ? 'PK' : ''}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 ${isLocked ? (inherited ? 'text-amber-600' : 'text-red-600') : 'text-gray-400 hover:text-gray-700'}`}
          title={inherited ? `Inherited lock from ${lock.node_label}. Edit that node to change.` : 'Lock / release this location for put-away / picking'}
          disabled={inherited}
          onClick={(e) => { e.stopPropagation(); openLockDialog(node); }}
          data-testid={`gs-lock-btn-${node.location_id}`}
        >
          {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </Button>
      </div>
    );
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getLocations();
      setLocations(data.locations || []);
      
      // Extract unique halls
      const uniqueHalls = [...new Set(data.locations.filter(l => l.hall_name).map(l => l.hall_name))];
      setHalls(uniqueHalls);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    fetchLocks();
    fetchLockSummary();
  }, []);

  // Fetch floors when hall changes
  useEffect(() => {
    if (selectedHall) {
      const hallFloors = [...new Set(
        locations
          .filter(l => l.hall_name === selectedHall && l.floor_name)
          .map(l => l.floor_name)
      )];
      setFloors(hallFloors);
      setSelectedFloor('');
      setRows([]);
      setSelectedRow('');
    }
  }, [selectedHall, locations]);

  // Fetch rows when floor changes
  useEffect(() => {
    if (selectedHall && selectedFloor) {
      const floorRows = [...new Set(
        locations
          .filter(l => l.hall_name === selectedHall && l.floor_name === selectedFloor && l.row_name)
          .map(l => l.row_name)
      )];
      setRows(floorRows);
      setSelectedRow('');
    }
  }, [selectedFloor, selectedHall, locations]);

  // Open the tree down to an existing location and flash it (used on "already exists").
  const revealLocation = (p) => {
    const parts = [p.hall_name, p.floor_name, p.row_name, p.position_name].filter(Boolean);
    const open = {};
    parts.slice(0, -1).forEach((_, i) => { open[parts.slice(0, i + 1).join(' > ')] = true; });
    const fp = parts.join(' > ');
    setExpanded((prev) => ({ ...prev, ...open }));
    setActiveTab('tree');
    setHighlight(fp);
    setTimeout(() => {
      document.querySelector(`[data-footprint="${CSS.escape(fp)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    setTimeout(() => setHighlight(''), 8000);
  };

  const handleCreate = async () => {
    const payload = {};
    try {
      
      if (createDialog.level === 'hall') {
        if (!formData.hall_name.trim()) {
          toast.error('Hall name is required');
          return;
        }
        payload.hall_name = formData.hall_name.trim();
        payload.description = formData.description;
      } else if (createDialog.level === 'floor') {
        if (!selectedHall || !formData.floor_name.trim()) {
          toast.error('Hall and Floor name are required');
          return;
        }
        payload.hall_name = selectedHall;
        payload.floor_name = formData.floor_name.trim();
        payload.description = formData.description;
      } else if (createDialog.level === 'row') {
        if (!selectedHall || !selectedFloor || !formData.row_name.trim()) {
          toast.error('Hall, Floor and Row name are required');
          return;
        }
        payload.hall_name = selectedHall;
        payload.floor_name = selectedFloor;
        payload.row_name = formData.row_name.trim();
        payload.description = formData.description;
      } else if (createDialog.level === 'position') {
        if (!selectedHall || !selectedFloor || !selectedRow || !formData.position_name.trim()) {
          toast.error('All fields are required for position');
          return;
        }
        payload.hall_name = selectedHall;
        payload.floor_name = selectedFloor;
        payload.row_name = selectedRow;
        payload.position_name = formData.position_name.trim();
        payload.capacity_kg = formData.capacity_kg ? parseFloat(formData.capacity_kg) : null;
        payload.description = formData.description;
      }
      
      await greigeStorageApi.createLocation(payload);
      toast.success('Location created');
      setCreateDialog({ open: false, level: 'hall' });
      setFormData({ hall_name: '', floor_name: '', row_name: '', position_name: '', capacity_kg: '', description: '' });
      fetchLocations();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && /already exists/i.test(detail || '')) {
        toast.error(`${detail} — highlighted in the hierarchy below`);
        setCreateDialog({ open: false, level: 'hall' });
        revealLocation(payload);
        return;
      }
      toast.error(detail || 'Failed to create location');
    }
  };

  const handleEdit = async () => {
    try {
      const payload = {};
      if (editData.capacity_kg !== '') payload.capacity_kg = parseFloat(editData.capacity_kg);
      if (editData.description !== '') payload.description = editData.description;
      if (editData.status !== '') payload.status = editData.status;
      
      await greigeStorageApi.updateLocation(editDialog.location.location_id, payload);
      toast.success('Location updated');
      setEditDialog({ open: false, location: null });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    try {
      await greigeStorageApi.deleteLocation(deleteDialog.location.location_id);
      toast.success('Location deleted');
      setDeleteDialog({ open: false, location: null });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleExpand = (footprint) => {
    setExpanded(prev => ({ ...prev, [footprint]: !prev[footprint] }));
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'hall': return <Building className="w-4 h-4" />;
      case 'floor': return <Layers className="w-4 h-4" />;
      case 'row': return <Grid3X3 className="w-4 h-4" />;
      case 'position': return <Box className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'hall': return 'bg-blue-100 text-blue-700';
      case 'floor': return 'bg-purple-100 text-purple-700';
      case 'row': return 'bg-orange-100 text-orange-700';
      case 'position': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Build tree structure
  const buildTree = () => {
    const tree = {};
    
    locations.forEach(loc => {
      if (loc.level === 'hall') {
        if (!tree[loc.hall_name]) {
          tree[loc.hall_name] = { ...loc, children: {} };
        }
      }
    });
    
    locations.forEach(loc => {
      if (loc.level === 'floor' && tree[loc.hall_name]) {
        if (!tree[loc.hall_name].children[loc.floor_name]) {
          tree[loc.hall_name].children[loc.floor_name] = { ...loc, children: {} };
        }
      }
    });
    
    locations.forEach(loc => {
      if (loc.level === 'row' && tree[loc.hall_name]?.children[loc.floor_name]) {
        if (!tree[loc.hall_name].children[loc.floor_name].children[loc.row_name]) {
          tree[loc.hall_name].children[loc.floor_name].children[loc.row_name] = { ...loc, children: [] };
        }
      }
    });
    
    locations.forEach(loc => {
      if (loc.level === 'position' && tree[loc.hall_name]?.children[loc.floor_name]?.children[loc.row_name]) {
        tree[loc.hall_name].children[loc.floor_name].children[loc.row_name].children.push(loc);
      }
    });
    
    return tree;
  };

  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expanded[node.footprint];
    const hasChildren = node.children && (Array.isArray(node.children) ? node.children.length > 0 : Object.keys(node.children).length > 0);
    
    return (
      <div key={node.footprint} className="select-none">
        <div 
          className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${highlight === node.footprint ? 'bg-amber-100 ring-2 ring-amber-400' : ''}`}
          style={{ marginLeft: `${level * 24}px` }}
          onClick={() => hasChildren && toggleExpand(node.footprint)}
          data-footprint={node.footprint}
          data-testid={`location-node-${node.location_id}`}
          data-highlighted={highlight === node.footprint ? 'true' : 'false'}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <span className="w-4" />
          )}
          
          <Badge className={`${getLevelColor(node.level)} border-0`}>
            {getLevelIcon(node.level)}
          </Badge>
          
          <span className="font-medium text-[#1A1F2E] flex-1">
            {node.position_name || node.row_name || node.floor_name || node.hall_name}
          </span>
          
          {node.level === 'position' && (
            <span className="text-xs text-gray-500">
              {node.bundle_count || 0} bundles • {node.current_weight_kg?.toFixed(1) || 0}/{node.capacity_kg || '∞'} kg
            </span>
          )}
          
          <LockControl node={node} />
          <div className="flex gap-1 opacity-0 group-hover:opacity-100">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setEditDialog({ open: true, location: node });
                setEditData({ 
                  capacity_kg: node.capacity_kg || '', 
                  description: node.description || '',
                  status: node.status || ''
                });
              }}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0 text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteDialog({ open: true, location: node });
              }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {Array.isArray(node.children) 
              ? node.children.map(child => renderTreeNode(child, level + 1))
              : Object.values(node.children).map(child => renderTreeNode(child, level + 1))
            }
          </div>
        )}
      </div>
    );
  };

  const tree = buildTree();

  // Stats
  const stats = {
    halls: locations.filter(l => l.level === 'hall').length,
    floors: locations.filter(l => l.level === 'floor').length,
    rows: locations.filter(l => l.level === 'row').length,
    positions: locations.filter(l => l.level === 'position').length,
    totalCapacity: locations.filter(l => l.level === 'position').reduce((sum, l) => sum + (l.capacity_kg || 0), 0),
    usedCapacity: locations.filter(l => l.level === 'position').reduce((sum, l) => sum + (l.current_weight_kg || 0), 0)
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Location Setup</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage Admin</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchLocations}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <Building className="w-5 h-5 mx-auto text-blue-500 mb-1" />
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.halls}</p>
              <p className="text-xs text-[#5A5F6E]">Halls</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <Layers className="w-5 h-5 mx-auto text-purple-500 mb-1" />
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.floors}</p>
              <p className="text-xs text-[#5A5F6E]">Floors</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <Grid3X3 className="w-5 h-5 mx-auto text-orange-500 mb-1" />
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.rows}</p>
              <p className="text-xs text-[#5A5F6E]">Rows</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <Box className="w-5 h-5 mx-auto text-green-500 mb-1" />
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.positions}</p>
              <p className="text-xs text-[#5A5F6E]">Positions</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.totalCapacity.toFixed(0)}</p>
              <p className="text-xs text-[#5A5F6E]">Total Capacity (kg)</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-[#1A1F2E]">{stats.usedCapacity.toFixed(1)}</p>
              <p className="text-xs text-[#5A5F6E]">Used (kg)</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Add Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={() => setCreateDialog({ open: true, level: 'hall' })} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Add Hall
          </Button>
          <Button onClick={() => setCreateDialog({ open: true, level: 'floor' })} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
            <Plus className="w-4 h-4 mr-1" /> Add Floor
          </Button>
          <Button onClick={() => setCreateDialog({ open: true, level: 'row' })} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50">
            <Plus className="w-4 h-4 mr-1" /> Add Row
          </Button>
          <Button onClick={() => setCreateDialog({ open: true, level: 'position' })} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" data-testid="gs-add-position-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Position
          </Button>
        </div>

        {/* Locked Positions Summary */}
        <Card className="border-[#E5DFD0] mb-6" data-testid="gs-locked-summary-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              Locked Locations Summary
              {lockSummary.length > 0 && <Badge className="bg-red-100 text-red-700 border-0">{lockSummary.length}</Badge>}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleResync} disabled={resyncing} data-testid="gs-lock-resync-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${resyncing ? 'animate-spin' : ''}`} /> Resync Stock Flags
            </Button>
          </CardHeader>
          <CardContent>
            {lockSummary.length === 0 ? (
              <div className="text-center py-6 text-[#5A5F6E]">
                <Unlock className="w-8 h-8 text-[#E5DFD0] mx-auto mb-2" />
                No locked locations. Use the lock icon on any node below to block put-away or picking.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#5A5F6E] border-b border-[#E5DFD0]">
                      <th className="py-2 pr-4">Locked Node</th>
                      <th className="py-2 pr-4">Scope</th>
                      <th className="py-2 pr-4">Blocked</th>
                      <th className="py-2 pr-4">Positions</th>
                      <th className="py-2 pr-4">Stored Bundles</th>
                      <th className="py-2 pr-4">Locked By</th>
                      <th className="py-2 pr-4">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockSummary.map((r) => (
                      <tr key={r.lock_id} className="border-b border-[#F0EBE0]" data-testid={`gs-locked-row-${r.lock_id}`}>
                        <td className="py-2 pr-4 font-medium text-[#1A1F2E]">{r.node_label}</td>
                        <td className="py-2 pr-4 capitalize">{r.scope}</td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-1">
                            {r.block_put_away && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Put-Away</span>}
                            {r.block_picking && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Picking</span>}
                          </div>
                        </td>
                        <td className="py-2 pr-4">{r.affected_positions}</td>
                        <td className="py-2 pr-4">{r.stored_bundles}</td>
                        <td className="py-2 pr-4 text-[#5A5F6E]">{r.locked_by || '—'}</td>
                        <td className="py-2 pr-4 text-[#5A5F6E]">{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location Tree */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location Hierarchy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : Object.keys(tree).length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Locations Configured</h3>
                <p className="text-[#5A5F6E] mb-4">Start by adding a Hall</p>
                <Button onClick={() => setCreateDialog({ open: true, level: 'hall' })} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]">
                  <Plus className="w-4 h-4 mr-2" /> Add First Hall
                </Button>
              </div>
            ) : (
              <div className="space-y-1 group">
                {Object.values(tree).map(hall => renderTreeNode(hall, 0))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialog.open} onOpenChange={(open) => !open && setCreateDialog({ open: false, level: 'hall' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">Add {createDialog.level}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {createDialog.level !== 'hall' && (
              <div>
                <Label>Hall *</Label>
                <Select value={selectedHall} onValueChange={setSelectedHall}>
                  <SelectTrigger className="mt-1" data-testid="gs-create-hall-select">
                    <SelectValue placeholder="Select Hall..." />
                  </SelectTrigger>
                  <SelectContent>
                    {halls.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(createDialog.level === 'row' || createDialog.level === 'position') && (
              <div>
                <Label>Floor *</Label>
                <Select value={selectedFloor} onValueChange={setSelectedFloor} disabled={!selectedHall}>
                  <SelectTrigger className="mt-1" data-testid="gs-create-floor-select">
                    <SelectValue placeholder="Select Floor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {floors.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {createDialog.level === 'position' && (
              <div>
                <Label>Row *</Label>
                <Select value={selectedRow} onValueChange={setSelectedRow} disabled={!selectedFloor}>
                  <SelectTrigger className="mt-1" data-testid="gs-create-row-select">
                    <SelectValue placeholder="Select Row..." />
                  </SelectTrigger>
                  <SelectContent>
                    {rows.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {createDialog.level === 'hall' && (
              <div>
                <Label>Hall Name *</Label>
                <Input
                  value={formData.hall_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, hall_name: e.target.value }))}
                  placeholder="e.g., Hall A"
                  className="mt-1"
                />
              </div>
            )}
            
            {createDialog.level === 'floor' && (
              <div>
                <Label>Floor Name *</Label>
                <Input
                  value={formData.floor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, floor_name: e.target.value }))}
                  placeholder="e.g., Ground Floor"
                  className="mt-1"
                />
              </div>
            )}
            
            {createDialog.level === 'row' && (
              <div>
                <Label>Row Name *</Label>
                <Input
                  value={formData.row_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, row_name: e.target.value }))}
                  placeholder="e.g., Row 1"
                  className="mt-1"
                />
              </div>
            )}
            
            {createDialog.level === 'position' && (
              <>
                <div>
                  <Label>Position Name *</Label>
                  <Input
                    value={formData.position_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, position_name: e.target.value }))}
                    placeholder="e.g., P01"
                    className="mt-1"
                    data-testid="gs-create-position-input"
                  />
                </div>
                <div>
                  <Label>Capacity (KG)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={formData.capacity_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, capacity_kg: e.target.value }))}
                    placeholder="e.g., 500.5 (leave empty for unlimited)"
                    className="mt-1"
                  />
                </div>
              </>
            )}
            
            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog({ open: false, level: 'hall' })}>
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="gs-create-submit-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, location: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium">{editDialog.location?.footprint}</p>
            </div>
            
            {editDialog.location?.level === 'position' && (
              <div>
                <Label>Capacity (KG)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={editData.capacity_kg}
                  onChange={(e) => setEditData(prev => ({ ...prev, capacity_kg: e.target.value }))}
                  placeholder="Leave empty for unlimited"
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label>Description</Label>
              <Input
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Status</Label>
              <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, location: null })}>
              Cancel
            </Button>
            <Button onClick={handleEdit} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock / Release Dialog */}
      <Dialog open={lockDialog.open} onOpenChange={(open) => !open && setLockDialog({ open: false, target: null })}>
        <DialogContent data-testid="gs-lock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" /> Lock / Release Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 capitalize">{lockDialog.target?.level}</p>
              <p className="font-medium">{lockDialog.target?.footprint}</p>
              {lockDialog.target?.level !== 'position' && (
                <p className="text-xs text-amber-700 mt-1">Locking this node cascades to every position under it.</p>
              )}
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium text-[#1A1F2E]">Block Put-Away</p>
                <p className="text-xs text-[#5A5F6E]">No put-away or transfer INTO this location</p>
              </div>
              <Switch checked={lockPutAway} onCheckedChange={setLockPutAway} data-testid="gs-lock-putaway-switch" />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium text-[#1A1F2E]">Block Picking</p>
                <p className="text-xs text-[#5A5F6E]">No picking or transfer OUT of this location</p>
              </div>
              <Switch checked={lockPicking} onCheckedChange={setLockPicking} data-testid="gs-lock-picking-switch" />
            </div>

            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                placeholder="e.g., Stock audit / damaged rack / reserved"
                className="mt-1"
                data-testid="gs-lock-reason-input"
              />
            </div>
            {!lockPutAway && !lockPicking && (
              <p className="text-xs text-[#5A5F6E]">Both switches off will RELEASE this location.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialog({ open: false, target: null })} data-testid="gs-lock-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={saveLock}
              disabled={savingLock}
              className={(!lockPutAway && !lockPicking) ? 'bg-[#2D5F4F] hover:bg-[#1A1F2E]' : 'bg-red-600 hover:bg-red-700'}
              data-testid="gs-lock-save-btn"
            >
              {(!lockPutAway && !lockPicking) ? 'Release Location' : 'Apply Lock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, location: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this location?</p>
            <div className="p-3 bg-red-50 rounded-lg mt-3">
              <p className="font-medium text-red-700">{deleteDialog.location?.footprint}</p>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              This action cannot be undone. Location must be empty and have no children.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, location: null })}>
              Cancel
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationSetup;
